import { useEffect, useState } from "react";
import { Activity, CheckCircle2, AlertTriangle, XCircle, RefreshCw } from "lucide-react";
import { fmtRelative } from "@/lib/format";

const BASE = process.env.REACT_APP_BACKEND_URL;
const LABEL = { operational: "All systems operational", degraded: "Partial degradation", down: "Major outage", unknown: "Checking…" };
const ICON = { operational: CheckCircle2, degraded: AlertTriangle, down: XCircle, unknown: Activity };

// Public, no-login status page. Deliberately minimal so it still renders when the rest of the app is unhappy.
export default function StatusPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [at, setAt] = useState(null);
  const load = async () => {
    try {
      const r = await fetch(`${BASE}/api/status`, { cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setD(await r.json()); setErr(null); setAt(new Date());
    } catch (e) { setErr(e.message || "unreachable"); setAt(new Date()); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);
  const overall = err ? "down" : d?.overall || "unknown";
  const Icon = ICON[overall] || Activity;
  const groups = d ? [...new Set(d.components.map((c) => c.group))] : [];
  return (
    <div className="tc-status" data-testid="status-page">
      <header className="tc-status__head">
        <div className="tc-status__brand"><span className="tc-status__logo">TC</span> Total Chat status</div>
        <a href="/client" className="tc-status__link" data-testid="status-open-app">Open Total Chat →</a>
      </header>
      <main className="tc-status__main">
        <div className={`tc-status__hero is-${overall}`} data-testid="status-overall" data-status={overall}>
          <Icon size={28} />
          <div>
            <h1>{err ? "Total Chat is unreachable" : LABEL[overall]}</h1>
            <p>{err ? `The status API could not be reached (${err}). The team's SMS alerts fire automatically on outages.` : d?.updated_at ? `Last checked ${fmtRelative(d.updated_at)} · ${d.open_incidents} open incident${d.open_incidents === 1 ? "" : "s"}` : "Collecting the first results…"}</p>
          </div>
          <button className="tc-status__refresh" onClick={load} title="Refresh" data-testid="status-refresh"><RefreshCw size={16} /></button>
        </div>
        {groups.map((g) => (
          <section key={g} className="tc-status__group">
            <h2>{g}</h2>
            <ul>
              {d.components.filter((c) => c.group === g).map((c) => (
                <li key={c.id} className={`tc-status__row is-${c.status}`} data-testid={`status-component-${c.id}`}>
                  <span>{c.name}{!c.critical && <small> · non-critical</small>}</span>
                  <span className="tc-status__pill"><i />{c.status === "operational" ? "Operational" : c.status === "degraded" ? "Degraded" : c.status === "down" ? "Down" : "Checking"}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {d && (
          <section className="tc-status__group">
            <h2>Recent incidents</h2>
            {d.recent_incidents.length ? (
              <ul>{d.recent_incidents.map((i, k) => <li key={k} className={`tc-status__row is-${i.resolved_at ? "operational" : i.status}`}><span>{i.name} — {i.status}</span><span className="muted">{i.resolved_at ? `resolved ${fmtRelative(i.resolved_at)}` : `since ${fmtRelative(i.started_at)}`}</span></li>)}</ul>
            ) : <p className="muted">No incidents recorded.</p>}
          </section>
        )}
        <footer className="tc-status__foot">
          {d?.last_backup_at && <span>Last data backup {fmtRelative(d.last_backup_at)}</span>}
          {d?.started_at && <span>Server up since {new Date(d.started_at).toLocaleString()}</span>}
          {at && <span>Page refreshed {at.toLocaleTimeString()} · auto-refreshes every 30s</span>}
        </footer>
      </main>
    </div>
  );
}
